import { CancelOutlined } from '@mui/icons-material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { DropzoneProps } from 'react-dropzone';
import TicketUploadedFilesTable, {
    TicketUploadedFilesTableProps,
} from 'src/app/components/tables/TicketUploadedFilesTable';
import file from 'src/app/constants/constants/file-types/file';
import { ErrorReturn } from 'src/app/hooks/useApiFetch';
import Toaster from 'src/app/utilities/helpers/Toaster';
import { zodRequiredStringSchema } from 'src/app/utilities/zod/zodRequiredStringSchema';
import TicketFile from 'src/data/models/common/ticketFile';
import Chip from 'src/view/components/chip/Chip';
import { Dropzone, DropzoneComponentProps } from 'src/view/components/dropzone-input/Dropzone';
import Popover from 'src/view/components/popover/Popover';
import TableHeader from 'src/view/components/table/table-header/TableHeader';
import z from 'zod';

const useStyles = makeStyles((theme) => ({
    fileUploadContainer: {
        padding: theme.spacing(1),
        '& p': {
            fontSize: '.625rem',
        },
        '& svg': {
            height: 12,
        },
    },
    fileUploadFormContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: theme.spacing(1),
    },
    topRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        gap: theme.spacing(1),
    },
    popoverContainer: {
        marginRight: theme.spacing(1),
    },
}));

export interface AddTicketLinkFormValues {
    ticketLink?: string;
}

export const addTicketLinkValidationSchema = z.object({
    ticketLink: zodRequiredStringSchema.url('Please copy a valid URL'),
});

export interface TicketUploadFilesFormProps {
    ticketFiles?: TicketFile[];
    dropzoneProps?: DropzoneProps & DropzoneComponentProps;
    onDeleteAll?: () => void;
    ticketUploadedFilesTableProps: TicketUploadedFilesTableProps;
    onUpload: (files: File[]) => void;
    onTicketLinkSaved: (values: AddTicketLinkFormValues) => Promise<void | ErrorReturn>;
}

export const TicketFilesForm = ({
    ticketFiles,
    dropzoneProps,
    ticketUploadedFilesTableProps,
    onTicketLinkSaved,
    onDeleteAll,
}: TicketUploadFilesFormProps) => {
    const classes = useStyles();
    const [isPasting, setIsPasting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const ticketsDataFiles = ticketFiles || [];

    const renderFilesPopover = () => {
        return (
            <Popover
                trigger="click"
                boxPadding={0}
                text={
                    <Chip
                        variant="outlined"
                        clickable
                        rounded
                        tinyChip
                        label={<div>Files attached ({ticketsDataFiles.length})</div>}
                        icon={<AttachFileOutlinedIcon />}
                        deleteIcon={<CancelOutlined style={{ marginLeft: 2, marginRight: 2 }} />}
                        onDelete={onDeleteAll}
                    />
                }
            >
                <>
                    <TableHeader
                        title={`Files attached (${ticketFiles?.length})`}
                        variant="small"
                    />
                    <TicketUploadedFilesTable
                        files={ticketsDataFiles}
                        {...ticketUploadedFilesTableProps}
                    />
                </>
            </Popover>
        );
    };

    const handlePasteFromClipboard = async () => {
        try {
            setIsPasting(true);
            const text = await navigator.clipboard.readText();

            if (!text || text.trim() === '') {
                enqueueSnackbar('Clipboard is empty, there’s no url to paste.', {
                    variant: 'error',
                });
                return;
            }

            Toaster.toast('Adding ticket link...', { variant: 'info' });

            const validationResult = await addTicketLinkValidationSchema.safeParseAsync({
                ticketLink: text,
            });

            if (!validationResult.success) {
                const errorMessage = validationResult.error.errors[0]?.message || 'Invalid link';
                enqueueSnackbar(errorMessage, { variant: 'error' });
                return;
            }

            await onTicketLinkSaved({ ticketLink: text });
        } catch (error) {
            const typedError = error as ErrorReturn;
            if (typedError.parsedErrors) {
                typedError.parsedErrors.forEach((x) =>
                    enqueueSnackbar(x.title, { variant: 'error' })
                );
            } else {
                console.error(error);
            }
        } finally {
            setIsPasting(false);
        }
    };

    const hasFilesUploaded = ticketsDataFiles.length > 0;

    return (
        <div className={classes.fileUploadFormContainer}>
            <div className={classes.topRow}>
                {hasFilesUploaded && (
                    <div className={classes.popoverContainer}>{renderFilesPopover()}</div>
                )}

                <Dropzone
                    uploadText={hasFilesUploaded ? <FileUploadIcon /> : "Drag 'n drop files here"}
                    dragActiveText={hasFilesUploaded ? <FileUploadIcon /> : 'Drop file(s)...'}
                    className={classes.fileUploadContainer}
                    accept={file.ticketFilesAcceptedMimeTypes}
                    {...dropzoneProps}
                />

                <Chip
                    variant="outlined"
                    clickable
                    rounded
                    tinyChip
                    label={<div>{'Paste link'}</div>}
                    icon={<ContentPasteIcon />}
                    onClick={handlePasteFromClipboard}
                    disabled={isPasting}
                />
            </div>
        </div>
    );
};
